import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { WebContainer } from '@webcontainer/api';
import { toastError, toastWarning } from '@ideoz/ui';
import type { HostToIframeEditMessage } from '../preview-bridge/protocol';
import { isIdeozEditMessage } from '../preview-bridge/protocol';
import {
  persistJsxAttributeLiteral,
  persistMergedClassName,
  persistSelectOptions,
  persistTextEdit,
} from '../preview-bridge/applyVisualEdit';
import { mergeTailwindFromCssEdit } from '../preview-bridge/tailwindPatch';
import { selectFile } from '../stores/editor';
import type { HotEditBridgeApi, HotEditSelection } from '../stores/hotEdit';
import { hotEditStore } from '../stores/hotEdit';
import type { HotEditElementKind } from '../preview-bridge/elementKind';
import { getEditableSections } from '../preview-bridge/elementKind';
import { workbenchStore } from '../stores/workbench';
import { ensureHotEditBridgeFiles } from '../utils/ensureHotEditBridge';

const DEBOUNCE_MS = 420;

/** Optional analytics bridge — host app may listen on `window` for `ideoz-hot-edit-analytics`. */
function emitHotEditAnalytics(eventName: string, properties?: Record<string, unknown>): void {
  try {
    window.dispatchEvent(new CustomEvent('ideoz-hot-edit-analytics', { detail: { eventName, properties } }));
  } catch {
    /* noop */
  }
}

/** False when iframe omits flags (legacy v1 bridges). */
function selectionCanPersistClasses(sel: HotEditSelection | null): boolean {
  if (!sel?.source?.filePath) return false;
  return sel.canPersistClasses !== false;
}

export function useHotEditBridge(opts: {
  previewUrl: string;
  iframeLoaded: boolean;
}): {
  previewIframeRef: RefObject<HTMLIFrameElement | null>;
  bridgeReady: boolean;
  toggleHotEditMode: () => Promise<void>;
  applyInstantCss: (elementId: string, property: string, value: string) => void;
  applyInstantText: (elementId: string, text: string) => void;
  scheduleApplyInstantText: (elementId: string, text: string) => void;
  applyInstantDom: (elementId: string, key: string, value: string) => void;
  queuePersistTypographyLayout: () => void;
  queuePersistText: (text: string) => void;
  queuePersistPlaceholder: (placeholder: string) => void;
  queuePersistDomValue: (value: string) => void;
  queuePersistSelectOptions: (options: string[]) => void;
  queuePersistInputType: (type: string) => void;
  bumpMergedFromTailwind: (property: string, value: string) => void;
  mergedClassDraftRef: MutableRefObject<string>;
  cnDeltaMergedRef: MutableRefObject<string>;
  exitHotEditMode: () => void;
  openSelectionInEditor: () => void;
} {
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [bridgeReady, setBridgeReady] = useState(false);
  const mergedClassDraftRef = useRef('');
  /** Extra utilities appended into `cn()` when persisting component usage sites */
  const cnDeltaMergedRef = useRef('');
  const persistTimerRef = useRef<number | null>(null);
  const textPersistTimerRef = useRef<number | null>(null);
  const pendingTextRef = useRef<{ selection: HotEditSelection; text: string } | null>(null);
  const placeholderPersistTimerRef = useRef<number | null>(null);
  const pendingPlaceholderRef = useRef<{ selection: HotEditSelection; placeholder: string } | null>(
    null,
  );
  const valuePersistTimerRef = useRef<number | null>(null);
  const pendingValueRef = useRef<{ selection: HotEditSelection; value: string } | null>(null);
  const selectOptionsPersistTimerRef = useRef<number | null>(null);
  const pendingSelectOptionsRef = useRef<{ selection: HotEditSelection; options: string[] } | null>(
    null,
  );
  const inputTypePersistTimerRef = useRef<number | null>(null);
  const pendingInputTypeRef = useRef<{ selection: HotEditSelection; type: string } | null>(null);

  type TypographyPersistJob = { selection: HotEditSelection; cnAppend: string };
  const pendingTypographyRef = useRef<TypographyPersistJob | null>(null);

  const textInstantRafRef = useRef<number | null>(null);
  const ensuredBridgeForWcRef = useRef<unknown>(null);
  const flushAllPendingRef = useRef<(() => Promise<void>) | null>(null);
  const pendingInstantTextRef = useRef<{ elementId: string; text: string } | null>(null);

  const postToIframe = useCallback(
    (msg: HostToIframeEditMessage) => {
      const win = previewIframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(msg, '*');
    },
    [],
  );

  const syncEditModeToIframe = useCallback(() => {
    postToIframe({ type: 'IDEOZ_EDIT_ENABLE', enabled: hotEditStore.mode.get() });
  }, [postToIframe]);

  const openSelectionInEditor = useCallback(() => {
    const sel = hotEditStore.selection.get();
    const path = sel?.source?.filePath;
    if (!path) return;
    selectFile(path);
    window.dispatchEvent(new CustomEvent('ideoz-workbench-open-code'));
  }, []);

  useEffect(() => {
    syncEditModeToIframe();
    const unsub = hotEditStore.mode.subscribe(() => {
      syncEditModeToIframe();
    });
    return unsub;
  }, [syncEditModeToIframe]);

  useEffect(() => {
    if (!opts.iframeLoaded) return;
    syncEditModeToIframe();
  }, [opts.iframeLoaded, syncEditModeToIframe]);

  /** Pointer left preview iframe (e.g. toward Hot edit panel) — iframe stops getting pointermove; clear stale hover box. */
  useEffect(() => {
    if (!opts.iframeLoaded) return;

    const attach = (): (() => void) | undefined => {
      const iframe = previewIframeRef.current;
      if (!iframe || !hotEditStore.mode.get()) return undefined;

      const clearHover = () => {
        postToIframe({ type: 'IDEOZ_EDIT_CLEAR_HOVER' });
      };

      iframe.addEventListener('mouseleave', clearHover);
      iframe.addEventListener('pointerleave', clearHover);
      return () => {
        iframe.removeEventListener('mouseleave', clearHover);
        iframe.removeEventListener('pointerleave', clearHover);
      };
    };

    let detach = attach();
    const unsubMode = hotEditStore.mode.subscribe(() => {
      detach?.();
      detach = attach();
    });

    return () => {
      unsubMode();
      detach?.();
    };
  }, [opts.iframeLoaded, postToIframe]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (!isIdeozEditMessage(ev.data)) return;
      const iframeWin = previewIframeRef.current?.contentWindow;
      if (iframeWin && ev.source !== iframeWin) return;
      const msg = ev.data as Record<string, unknown>;
      switch (msg.type) {
        case 'IDEOZ_EDIT_READY':
          setBridgeReady(true);
          hotEditStore.iframePathname.set(String(msg.pathname ?? '/'));
          syncEditModeToIframe();
          break;
        case 'IDEOZ_EDIT_SELECT': {
          void flushAllPendingRef.current?.();

          const kindRaw = msg.elementKind;
          const ek = typeof kindRaw === 'string' ? (kindRaw as HotEditElementKind) : undefined;
          const elementKindResolved: HotEditElementKind = ek ?? 'container';

          mergedClassDraftRef.current = String(msg.className ?? '');
          cnDeltaMergedRef.current = '';

          const listCtx: HotEditSelection['listContext'] = ((): HotEditSelection['listContext'] => {
            const raw = msg.listContext;
            if (raw == null || typeof raw !== 'object') return null;
            const o = raw as Record<string, unknown>;
            return {
              inMapLoop: o.inMapLoop === true,
              mapRowIndex:
                typeof o.mapRowIndex === 'number'
                  ? o.mapRowIndex
                  : o.mapRowIndex != null && String(o.mapRowIndex).length
                    ? Number(o.mapRowIndex)
                    : null,
              reactKey: typeof o.reactKey === 'string' ? o.reactKey : o.reactKey == null ? null : String(o.reactKey),
              domNthOfType:
                typeof o.domNthOfType === 'number'
                  ? o.domNthOfType
                  : o.domNthOfType != null && String(o.domNthOfType).length
                    ? Number(o.domNthOfType)
                    : null,
              selectorPath:
                typeof o.selectorPath === 'string' ? o.selectorPath : undefined,
            };
          })();

          const hasListBinding = listCtx?.inMapLoop === true;

          const baseSections = getEditableSections(elementKindResolved);
          const editableSections =
            hasListBinding && !baseSections.includes('content')
              ? ['content' as const, ...baseSections]
              : baseSections;

          hotEditStore.selection.set({
            elementId: String(msg.elementId ?? ''),
            tagName: String(msg.tagName ?? ''),
            ownerComponentName:
              typeof msg.ownerComponentName === 'string' ? msg.ownerComponentName : undefined,
            source: (msg.source ?? null) as HotEditSelection['source'],
            implementationSource:
              (msg.implementationSource as HotEditSelection['implementationSource']) ?? undefined,
            selectorPath: typeof msg.selectorPath === 'string' ? msg.selectorPath : undefined,
            listContext: listCtx,
            computedStyles: (msg.computedStyles ?? {}) as HotEditSelection['computedStyles'],
            textContent: String(msg.textContent ?? ''),
            className: String(msg.className ?? ''),
            hasChildElements: Boolean(msg.hasChildElements),
            elementKind: ek,
            editTargetKind: msg.editTargetKind as HotEditSelection['editTargetKind'],
            canPersistClasses:
              typeof msg.canPersistClasses === 'boolean'
                ? Boolean(msg.canPersistClasses)
                : Boolean(msg.source),
            blockReason: msg.blockReason == null ? null : String(msg.blockReason),
            inputPlaceholder:
              typeof msg.inputPlaceholder === 'string' ? msg.inputPlaceholder : undefined,
            inputValue: typeof msg.inputValue === 'string' ? msg.inputValue : undefined,
            inputType: typeof msg.inputType === 'string' ? msg.inputType : undefined,
            optionValues: Array.isArray(msg.optionValues)
              ? (msg.optionValues as unknown[]).map((v) => String(v))
              : undefined,
            editableSections,
          });
          break;
        }
        case 'IDEOZ_EDIT_DESELECT':
          hotEditStore.selection.set(null);
          mergedClassDraftRef.current = '';
          cnDeltaMergedRef.current = '';
          break;
        case 'IDEOZ_EDIT_ROUTE':
          hotEditStore.iframePathname.set(String(msg.pathname ?? '/'));
          break;
        case 'IDEOZ_EDIT_HMR_IDLE': {
          const sel = hotEditStore.selection.get();
          if (!sel) break;
          postToIframe({ type: 'IDEOZ_EDIT_CLEAR_INLINE', elementId: sel.elementId });
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [postToIframe, syncEditModeToIframe]);

  useEffect(() => {
    setBridgeReady(false);
  }, [opts.previewUrl]);

  useEffect(() => {
    if (!opts.iframeLoaded) setBridgeReady(false);
  }, [opts.iframeLoaded]);

  const clearPersistTimers = useCallback(() => {
    if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    if (textPersistTimerRef.current) window.clearTimeout(textPersistTimerRef.current);
    if (placeholderPersistTimerRef.current) window.clearTimeout(placeholderPersistTimerRef.current);
    if (valuePersistTimerRef.current) window.clearTimeout(valuePersistTimerRef.current);
    if (selectOptionsPersistTimerRef.current) window.clearTimeout(selectOptionsPersistTimerRef.current);
    if (inputTypePersistTimerRef.current) window.clearTimeout(inputTypePersistTimerRef.current);
    persistTimerRef.current = null;
    textPersistTimerRef.current = null;
    placeholderPersistTimerRef.current = null;
    valuePersistTimerRef.current = null;
    selectOptionsPersistTimerRef.current = null;
    inputTypePersistTimerRef.current = null;
  }, []);

  const flushTypographyPersist = useCallback(async () => {
    persistTimerRef.current = null;
    const job = pendingTypographyRef.current;
    pendingTypographyRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const sel = job.selection;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const cnAppendOnly = job.cnAppend.trim();
    if (!cnAppendOnly) return;

    const r = await persistMergedClassName({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      cnAppendOnly,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
      componentName: sel.ownerComponentName,
      listContext: sel.listContext,
    });

    if (!r.ok) {
      toastWarning(
        r.error,
        'Inline preview remains until fixed in Code view.',
        'Open file',
        openSelectionInEditor,
      );
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'className', path: sel.source!.filePath });
  }, [openSelectionInEditor]);

  const flushTextPersist = useCallback(async () => {
    textPersistTimerRef.current = null;
    const job = pendingTextRef.current;
    pendingTextRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, text } = job;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const r = await persistTextEdit({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
      componentName: sel.ownerComponentName,
      hintText: sel.textContent,
      listContext: sel.listContext,
      text,
    });

    if (!r.ok) {
      toastError(r.error);
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', {
      kind: 'text',
      path: r.writtenFilePath ?? sel.source!.filePath,
    });
  }, []);

  const flushPlaceholderPersist = useCallback(async () => {
    placeholderPersistTimerRef.current = null;
    const job = pendingPlaceholderRef.current;
    pendingPlaceholderRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, placeholder } = job;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'placeholder',
      literalValue: placeholder,
    });

    if (!r.ok) {
      toastWarning(r.error, 'Placeholder stays inline in preview until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'placeholder', path: sel.source!.filePath });
  }, []);

  const flushValuePersist = useCallback(async () => {
    valuePersistTimerRef.current = null;
    const job = pendingValueRef.current;
    pendingValueRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, value: val } = job;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'defaultValue',
      literalValue: val,
    });

    if (!r.ok) {
      toastWarning(
        r.error,
        'Controlled inputs need Code edits — preview value stays inline.',
        'Open file',
        openSelectionInEditor,
      );
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'defaultValue', path: sel.source!.filePath });
  }, [openSelectionInEditor]);

  const flushSelectOptionsPersist = useCallback(async () => {
    selectOptionsPersistTimerRef.current = null;
    const job = pendingSelectOptionsRef.current;
    pendingSelectOptionsRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, options } = job;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const r = await persistSelectOptions({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      options,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
    });

    if (!r.ok) {
      toastWarning(r.error, 'Options stay inline in preview until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'selectOptions', path: sel.source!.filePath });
  }, []);

  const flushInputTypePersist = useCallback(async () => {
    inputTypePersistTimerRef.current = null;
    const job = pendingInputTypeRef.current;
    pendingInputTypeRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, type } = job;
    if (!wc || !selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'type',
      literalValue: type,
    });

    if (!r.ok) {
      toastWarning(r.error, 'Input type stays inline until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
    }
  }, []);

  const flushAllPending = useCallback(async () => {
    clearPersistTimers();
    await Promise.all([
      flushTypographyPersist(),
      flushTextPersist(),
      flushPlaceholderPersist(),
      flushValuePersist(),
      flushSelectOptionsPersist(),
      flushInputTypePersist(),
    ]);
  }, [
    clearPersistTimers,
    flushTypographyPersist,
    flushTextPersist,
    flushPlaceholderPersist,
    flushValuePersist,
    flushSelectOptionsPersist,
    flushInputTypePersist,
  ]);

  flushAllPendingRef.current = flushAllPending;

  const queuePersistTypographyLayout = useCallback(() => {
    const sel = hotEditStore.selection.get();
    if (!selectionCanPersistClasses(sel)) return;

    pendingTypographyRef.current = {
      selection: sel!,
      cnAppend: cnDeltaMergedRef.current,
    };

    if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => void flushTypographyPersist(), DEBOUNCE_MS);
  }, [flushTypographyPersist]);

  const queuePersistText = useCallback(
    (text: string) => {
      const sel = hotEditStore.selection.get();
      if (!selectionCanPersistClasses(sel)) return;

      pendingTextRef.current = { selection: sel!, text };

      if (textPersistTimerRef.current) window.clearTimeout(textPersistTimerRef.current);
      textPersistTimerRef.current = window.setTimeout(() => void flushTextPersist(), DEBOUNCE_MS);
    },
    [flushTextPersist],
  );

  const queuePersistPlaceholder = useCallback(
    (placeholder: string) => {
      const sel = hotEditStore.selection.get();
      if (!selectionCanPersistClasses(sel)) return;

      pendingPlaceholderRef.current = { selection: sel!, placeholder };

      if (placeholderPersistTimerRef.current) window.clearTimeout(placeholderPersistTimerRef.current);
      placeholderPersistTimerRef.current = window.setTimeout(
        () => void flushPlaceholderPersist(),
        DEBOUNCE_MS,
      );
    },
    [flushPlaceholderPersist],
  );

  const queuePersistDomValue = useCallback(
    (value: string) => {
      const sel = hotEditStore.selection.get();
      if (!selectionCanPersistClasses(sel)) return;

      pendingValueRef.current = { selection: sel!, value };

      if (valuePersistTimerRef.current) window.clearTimeout(valuePersistTimerRef.current);
      valuePersistTimerRef.current = window.setTimeout(() => void flushValuePersist(), DEBOUNCE_MS);
    },
    [flushValuePersist],
  );

  const queuePersistSelectOptions = useCallback(
    (options: string[]) => {
      const sel = hotEditStore.selection.get();
      if (!selectionCanPersistClasses(sel)) return;

      pendingSelectOptionsRef.current = { selection: sel!, options };

      if (selectOptionsPersistTimerRef.current)
        window.clearTimeout(selectOptionsPersistTimerRef.current);
      selectOptionsPersistTimerRef.current = window.setTimeout(
        () => void flushSelectOptionsPersist(),
        DEBOUNCE_MS,
      );
    },
    [flushSelectOptionsPersist],
  );

  const queuePersistInputType = useCallback(
    (type: string) => {
      const sel = hotEditStore.selection.get();
      if (!selectionCanPersistClasses(sel)) return;

      pendingInputTypeRef.current = { selection: sel!, type };

      if (inputTypePersistTimerRef.current) window.clearTimeout(inputTypePersistTimerRef.current);
      inputTypePersistTimerRef.current = window.setTimeout(
        () => void flushInputTypePersist(),
        DEBOUNCE_MS,
      );
    },
    [flushInputTypePersist],
  );

  const bumpMergedFromTailwind = useCallback((property: string, value: string) => {
    mergedClassDraftRef.current = mergeTailwindFromCssEdit(
      mergedClassDraftRef.current,
      property,
      value,
    );
    cnDeltaMergedRef.current = mergeTailwindFromCssEdit(cnDeltaMergedRef.current, property, value);
  }, []);

  const applyInstantCss = useCallback(
    (elementId: string, property: string, value: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'style', property, value },
      });
    },
    [postToIframe],
  );

  const applyInstantText = useCallback(
    (elementId: string, text: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'text', value: text },
      });
    },
    [postToIframe],
  );

  const scheduleApplyInstantText = useCallback(
    (elementId: string, text: string) => {
      pendingInstantTextRef.current = { elementId, text };
      if (textInstantRafRef.current != null) return;
      textInstantRafRef.current = window.requestAnimationFrame(() => {
        textInstantRafRef.current = null;
        const p = pendingInstantTextRef.current;
        if (!p) return;
        postToIframe({
          type: 'IDEOZ_EDIT_APPLY',
          elementId: p.elementId,
          patch: { kind: 'text', value: p.text },
        });
      });
    },
    [postToIframe],
  );

  const applyInstantDom = useCallback(
    (elementId: string, key: string, value: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'dom', key, value },
      });
    },
    [postToIframe],
  );

  const exitHotEditMode = useCallback(() => {
    void flushAllPendingRef.current?.();
    hotEditStore.mode.set(false);
    hotEditStore.selection.set(null);
    mergedClassDraftRef.current = '';
    cnDeltaMergedRef.current = '';
    postToIframe({ type: 'IDEOZ_EDIT_ENABLE', enabled: false });
  }, [postToIframe]);

  const toggleHotEditMode = useCallback(async () => {
    const wc = workbenchStore.webcontainer.get();
    const next = !hotEditStore.mode.get();
    if (next) {
      if (wc && ensuredBridgeForWcRef.current !== wc) {
        await ensureHotEditBridgeFiles(wc);
        ensuredBridgeForWcRef.current = wc;
      }
      emitHotEditAnalytics('hot_edit_enabled', { pathname: hotEditStore.iframePathname.get() });
      hotEditStore.mode.set(true);
      return;
    }
    exitHotEditMode();
    emitHotEditAnalytics('hot_edit_disabled', {});
  }, [exitHotEditMode]);

  const apiStable = useRef<HotEditBridgeApi | null>(null);
  apiStable.current = {
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    bumpMergedFromTailwind,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    openSelectionInEditor,
    exitHotEditMode,
  };

  useEffect(() => {
    const api = apiStable.current!;
    hotEditStore.bridgeApi.set(api);
    return () => hotEditStore.bridgeApi.set(null);
  }, [
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    bumpMergedFromTailwind,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    openSelectionInEditor,
    exitHotEditMode,
  ]);

  useEffect(() => {
    return () => {
      void flushAllPendingRef.current?.();
      clearPersistTimers();
      if (textInstantRafRef.current != null) window.cancelAnimationFrame(textInstantRafRef.current);
    };
  }, [clearPersistTimers]);

  return {
    previewIframeRef,
    bridgeReady,
    toggleHotEditMode,
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    bumpMergedFromTailwind,
    mergedClassDraftRef,
    cnDeltaMergedRef,
    exitHotEditMode,
    openSelectionInEditor,
  };
}
